import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Table, TableColumnsType, Tooltip } from 'antd';
import cx from 'classnames';
import { ICountryWithZone, IRateListRespEntryV2 } from '../../../interfaces';
import { useCountriesWithZoneListByZoneId, useRateInfo, useRateListV2, useUiIsLoading } from '../../../hooks';
import { tF, tI } from '../../../translate';
import { CardLayout, Catcher, cardLayoutOptions, viewOption } from '../../../Components';
import { IShowRateZoneTable, ShowRateCountryNetworksTableV2, ShowRateItem } from '../Components';
import '../../../customStyle/table.less';
import StylesItem from '../m_styles.less';

export type ShowRateZoneTableDataType = {
    key: React.Key;
    country: ReactNode;
    countryId: string;
    countryRate?: IRateListRespEntryV2;
    countryRates: IRateListRespEntryV2[];
    incrementDataShow?: ReactNode;
    networksCount: number;
    networksCountShow: ReactNode;
    operatorId: string;
    rateDataShow: ReactNode;
    rateSmsShow: ReactNode;
    rateVoiceMobileShow: ReactNode;
};

const columns: TableColumnsType<ShowRateZoneTableDataType> = [
    {
        title: '',
        dataIndex: 'networksCountShow',
        className: StylesItem.rateColumnVal,
        width: viewOption.rateTable.widthNetCount,
    },
    {
        title: tF('Country'),
        dataIndex: 'country',
        className: StylesItem.rateColumnCountry,
        width: viewOption.rateTable.widthRateCountry,
    },
    {
        title: tF('Increment Data'),
        dataIndex: 'incrementDataShow',
        className: StylesItem.rateColumnVal,
        width: viewOption.rateTable.widthRateVal,
    },
    {
        title: tF('Data'),
        dataIndex: 'rateDataShow',
        className: StylesItem.rateColumnVal,
        width: viewOption.rateTable.widthRateVal,
    },
    {
        title: tF('SMS'),
        dataIndex: 'rateSmsShow',
        className: StylesItem.rateColumnVal,
        width: viewOption.rateTable.widthRateVal,
    },
    {
        title: tF('Voice Mobile'),
        dataIndex: 'rateVoiceMobileShow',
        className: StylesItem.rateColumnVal,
        width: viewOption.rateTable.widthRateVal,
    },
    {
        title: tF('Voice Fix Net'),
        dataIndex: 'rateVoiceNetShow',
        className: StylesItem.rateColumnVal,
        width: viewOption.rateTable.widthRateVal,
    },
];

export const ShowRateZoneTableV2: React.FC<IShowRateZoneTable> = observer(
    ({ zoneId, name, operatorId }: IShowRateZoneTable) => {
        const isLoading = useUiIsLoading();
        const rateInfo = useRateInfo();
        const zoneCountries = useCountriesWithZoneListByZoneId(zoneId);
        const zoneRates = useRateListV2().filter(
            (r: IRateListRespEntryV2) => r.operatorId === operatorId && r.zoneId === zoneId,
        );

        const tableData: ShowRateZoneTableDataType[] = zoneCountries
            .filter((c) => c.zoneId === zoneId)
            .map((c: ICountryWithZone) => {
                const key = c.id;
                const countryRates = zoneRates.filter((r) => r.countryId === c.id);
                const countryRate = countryRates.find((r: IRateListRespEntryV2) => r.networkId === undefined);
                const countryNetworksRates = countryRates.filter(
                    (r: IRateListRespEntryV2) => r.networkId !== undefined,
                );
                const networksCount = countryNetworksRates.length;

                const NetworksCountShow = !!networksCount ? (
                    <Tooltip title={`${networksCount} ${tI('custom networks')}`}>
                        <span className={!networksCount ? StylesItem.defaultRate : StylesItem.customRate}>
                            {networksCount}
                        </span>
                    </Tooltip>
                ) : null;

                return {
                    key,
                    incrementDataShow: <span className={StylesItem.defaultRate}>{countryRate?.incrementData}</span>,
                    countryId: c.id,
                    countryRate: {
                        ...countryRate,
                        id: `${operatorId}-${zoneId}-${c.id}`,
                        currency: rateInfo.currency,
                    },
                    countryRates: countryNetworksRates,
                    country: (
                        <span className={!networksCount ? StylesItem.defaultRate : StylesItem.customRate}>
                            {c.name}
                        </span>
                    ),
                    operatorId,
                    networksCount,
                    networksCountShow: NetworksCountShow,
                    rateDataShow: (
                        <ShowRateItem
                            calculatedRate={countryRate?.calculatedRateData}
                            calculatedRateParent={rateInfo?.calculatedRateData}
                            currency={rateInfo.currency}
                            rate={countryRate?.rateData}
                            rateUnitId={countryRate?.rateDataUnitId}
                        />
                    ),
                    rateSmsShow: (
                        <ShowRateItem
                            calculatedRate={countryRate?.calculatedRateSms}
                            calculatedRateParent={rateInfo?.calculatedRateSms}
                            currency={rateInfo.currency}
                            rate={countryRate?.rateSms}
                            rateUnitId={countryRate?.rateSmsUnitId}
                        />
                    ),
                    rateVoiceMobileShow: (
                        <ShowRateItem
                            calculatedRate={countryRate?.calculatedRateVoiceMobile}
                            calculatedRateParent={rateInfo?.calculatedRateVoiceMobile}
                            currency={rateInfo.currency}
                            rate={countryRate?.rateVoiceMobile}
                            rateUnitId={countryRate?.rateVoiceMobileUnitId}
                        />
                    ),
                    rateVoiceNetShow: (
                        <ShowRateItem
                            calculatedRate={countryRate?.calculatedRateVoiceNet}
                            calculatedRateParent={rateInfo?.calculatedRateVoiceNet}
                            currency={rateInfo.currency}
                            rate={countryRate?.rateVoiceNet}
                            rateUnitId={countryRate?.rateVoiceNetUnitId}
                        />
                    ),
                };
            });

        const expandedRowRender = (record: ShowRateZoneTableDataType): JSX.Element => {
            return <ShowRateCountryNetworksTableV2 record={record} />;
        };

        return (
            <Catcher>
                <CardLayout
                    columnOption={cardLayoutOptions.cardTableOperators}
                    count={zoneCountries.length}
                    idSpinners={['GET_SALES_RATE_LIST']}
                    isTable
                    titleRight={`${name}`}
                >
                    <Table
                        className={cx('table-theme')}
                        columns={columns}
                        dataSource={tableData}
                        expandable={{
                            expandedRowRender,
                            rowExpandable: (record: ShowRateZoneTableDataType): boolean => !!record.networksCount,
                        }}
                        loading={isLoading}
                        pagination={false}
                        size={'small'}
                    />
                </CardLayout>
            </Catcher>
        );
    },
);
